import { GameCategory, GameErrorCodes, LicenseType, ToGamePostMessage } from '../../enums';
import { ChangeGamePayload, GameErrorPayload, GameReadyPayload } from '../../models';
import { toggleFullscreen, isSameMode } from '../../helpers';
import { GameModeName, PGCOIN } from '../../constants';
import { LaunchData } from '../../models';
import { env } from '../../environment';
import { RootStore } from '../../store';
import { getLaunchToken } from '../launch-telemetry';

export class IntegrationBase {

  protected rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * The access token to hand a frame. launchData.tokenInfo is the launch-time token and is never updated by a refresh,
   * so a frame that asked for auth after the first refresh (slot history, lobby, tournament, a reloaded game) got an
   * expired token and 401'd. The auth store holds the live one; launchData is only the fallback for launches it never
   * seeded (unauthenticated, cookie-based).
   */
  protected getAccessToken(launchData: LaunchData): string | undefined {
    return this.rootStore.authStore.authInfo?.accessToken ?? launchData.tokenInfo?.accessToken;
  }

  public updateAuthInfo(launchData: LaunchData | null) {
    if (!launchData) return;
    const { isAuthenticated, hasJackpot } = launchData;
    const authPayload = {
      name: ToGamePostMessage.AuthInfoUpdate,
      accessToken: this.getAccessToken(launchData),
      isAuthenticated,
      payload: { hasJackpot }
    };
    this.rootStore.postMessageStore.sendMessage(authPayload);
    if (this.rootStore.gameStore.launchData.data?.hasTournament) {
      this.rootStore.postMessageStore.sendTournamentMessage(authPayload);
    }
    if (this.rootStore.gameStore.launchData.data?.lobbyId) {
      this.rootStore.postMessageStore.sendLobbyMessage(authPayload);
    }
    if (this.rootStore.slotHistoryStore.isSlotHistoryOpen) {
      this.rootStore.postMessageStore.sendSlotHistoryMessage(authPayload);
    }
  }

  public hideGlobalLoader() {
    this.rootStore.loaderStore.hideLoader();
  }

  public handleGameLoaded(launchData: LaunchData | null, stakeAmount = 0) {
    if (launchData?.showPayTable) {
      this.rootStore.postMessageStore.sendOpenPayTable();
    }
    if (launchData?.licenseType === LicenseType.BrazilGLA) {
      this.rootStore.postMessageStore.sendIsBrazilGLA();
    }
    if (this.rootStore.sidebarMenuStore.showSidebar) {
      this.rootStore.sidebarMenuStore.sendSidebarPostMessage();
    }
  }

  public handleGameReady(data?: GameReadyPayload) {
    this.rootStore.gameStore.setGameReady(true);
    if (data) {
      const { balance, defaultAmount, canvasWidth } = data;
      if (data.hasOwnProperty('sound') || data.hasOwnProperty('fastPlay')) {
        this.handleSettingsUpdated(data);
      }
      if (balance || balance === 0) {
        this.handleBalanceUpdated(data);
      }
      if (defaultAmount) {
        this.handleStakeUpdate({ stake: defaultAmount });
      }
      if (canvasWidth) {
        this.rootStore.gameStore.setCanvasWidth(canvasWidth);
      }
    }
  }

  public handleErrorReceived(data: GameErrorPayload, refreshError = false) {
    const { errorCode } = data;
    switch (errorCode) {
      case GameErrorCodes.GameIsOpen:
        this.rootStore.freeDropStore.setFreeDropInfo(null);
        this.rootStore.modalStore.openGameIsOpenedModal(data);
        break;
      case GameErrorCodes.ConnectionError:
        this.rootStore.modalStore.openLostConnectionModal(data);
        break;
      case GameErrorCodes.NotEnoughMoney:
        this.rootStore.modalStore.openNotEnoughMoneyModal(data);
        break;
      case GameErrorCodes.PlayerLimitExceeded:
        this.rootStore.modalStore.openPlayerLimitExceededModal(data);
        break;
      case GameErrorCodes.GeneralError:
        this.rootStore.modalStore.openGeneralErrorModal(data);
        break;
      case GameErrorCodes.SessionExpired:
        this.rootStore.modalStore.openSessionTerminatedModal(data);
        break;
      case GameErrorCodes.OperatorError:
        this.rootStore.modalStore.openOperatorErrorModal(data);
        break;
    }
  }

  public generateLaunchUrl(data: LaunchData): string {

    const {
      url: launchUrl, urlPrefix, gameId, gameTypeId, mode, partnerId, userId, tournamentId, tournamentInfo, currency, currencySymbol, culture,
      playerExternalId, realModeSwitcher, theme, exitUrl, hidePlayerId, licenseType, skipSpinAfterBetChange, useFrontendBalanceCheck, category, hasPascalLoader
    } = data;

    const urlString = launchUrl || `${env.origin}/${urlPrefix}/`;

    const url = new URL(urlString);

    const isTournamentMode = tournamentId && tournamentId === tournamentInfo?.id;

    url.searchParams.set('partnerId', `${partnerId}`);
    url.searchParams.set('currency', `${isTournamentMode ? PGCOIN : currency}`);
    url.searchParams.set('currencySymbol', `${isTournamentMode ? PGCOIN : decodeURIComponent(currencySymbol)}`);
    url.searchParams.set('culture', `${culture}`);
    url.searchParams.set('userId', `${userId}`);
    url.searchParams.set('playerExternalId', `${playerExternalId}`);
    url.searchParams.set('gameId', `${gameId}`);
    url.searchParams.set('gameTypeId', `${gameTypeId}`);
    url.searchParams.set('mode', `${GameModeName[mode]}`);
    url.searchParams.set('theme', `${theme}`);
    url.searchParams.set('showLoader', `${hasPascalLoader ? 0 : 1}`);
    // Lets slot-lines ingress logs join our launch trace; off until they confirm they ignore the param.
    if (env.gameLaunchIdParam && getLaunchToken()) {
      url.searchParams.set('launchId', getLaunchToken());
    }
    if (exitUrl) {
      url.searchParams.set('exitUrl', encodeURIComponent(exitUrl));
    }
    if (tournamentId) {
      url.searchParams.set('tournamentId', `${tournamentId}`);
    }
    if (licenseType) {
      url.searchParams.set('licenseType', `${licenseType}`);
    }
    if (realModeSwitcher) {
      url.searchParams.set('realModeSwitcher', '1');
    }
    if (hidePlayerId) {
      url.searchParams.set('hidePlayerId', '1');
    }
    if (skipSpinAfterBetChange) {
      url.searchParams.set('skipSpinAfterBetChange', '1');
    }
    if (useFrontendBalanceCheck) {
      url.searchParams.set('checkBalanceOnBet', '1');
    }

    // TODO: remove hardcode
    if (category === GameCategory.CryptoGames && (+partnerId === 637 || +partnerId === 1476 ||
      +partnerId === 53 || +partnerId === 1378)) {
      url.searchParams.set('skin', 'vbet');
    }
    // TODO: remove hardcode
    if (+partnerId === 1) {
      url.searchParams.set('skin', 'vbetam');
    }
    // TODO: remove hardcode
    if (category === GameCategory.CryptoGames && +partnerId === 1266) {
      url.searchParams.set('skin', 'bfth');
    }
    // TODO: remove hardcode
    if (+partnerId === 2352 && (gameId === 38 || gameId === 42 || gameId === 43)) {
      url.searchParams.set('skin', 'limonBet');
    }
    // TODO: remove hardcode: 1324
    if (+partnerId === 1324 && gameId === 51) {
      url.searchParams.set('skin', 'ebets');
    }
    // TODO: remove hardcode: 1484
    if (+partnerId === 1484 && gameId === 36) {
      url.searchParams.set('skin', 'pbets');
    }
    // TODO: remove hardcode: 425
    if (+partnerId === 425 && gameId === 46) {
      url.searchParams.set('skin', 'betForward');
    }
    // TODO: remove hardcode: 936
    if (+partnerId === 936 && gameId === 36) {
      url.searchParams.set('skin', 'aloBet');
    }
    // TODO: remove hardcode: 205
    if (+partnerId === 205 && gameId === 36) {
      url.searchParams.set('skin', 'betzula');
    }

    return url.toString();
  }

  public registerFullscreenChangeListener() {
    const htmlElement = document.documentElement;
    const sendFullscreenUpdate = this.rootStore.postMessageStore.sendFullscreenUpdate.bind(this.rootStore.postMessageStore);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    });

    htmlElement.addEventListener('fullscreenchange', sendFullscreenUpdate);
    htmlElement.addEventListener('mozfullscreenchange', sendFullscreenUpdate);
    htmlElement.addEventListener('webkitfullscreenchange', sendFullscreenUpdate);
    htmlElement.addEventListener('msfullscreenchange', sendFullscreenUpdate);
  }

  public handleToggleFullScreen() {
    toggleFullscreen();
  }

  public handleRoundStarted(payload?: any) {
    this.rootStore.gameStore.setRoundInProgress(true);
  }

  public handleRoundFinished(payload?: any) {
    if (this.rootStore.jackpotStore.openJackpotSceneAfterRoundFinished) {
      this.rootStore.jackpotStore.showJackpotScene();
      this.rootStore.jackpotStore.setOpenJackpotSceneAfterRoundFinished(false)
    }
    if (this.rootStore.boostedJackpotStore.openBoostedJackpotAfterRoundFinished) {
      this.rootStore.boostedJackpotStore.showBoostedJackpotWheel();
      this.rootStore.boostedJackpotStore.setOpenBoostedJackpotAfterRoundFinished(false);
    }
    this.rootStore.gameStore.setRoundInProgress(false);
  }

  public handleSettingsUpdated(payload?: { sound?: boolean, fastPlay?: boolean }) {
    if (payload?.hasOwnProperty('sound')) {
      this.rootStore.gameStore.handleSoundEnabledUpdate(!!payload?.sound);
    }
  }

  public handleBalanceUpdated(payload?: any) { }

  public handleWinUpdate(payload?: any) { }

  public handleStakeUpdate(payload?: any) {
    if (payload?.stake) {
      this.rootStore.gameStore.setStakeAmount(payload.stake);
    }
  }

  public handlePlayModeUpdate(payload?: any) { }

  public handleAutoPlayStarted() { }

  public handleAutoPlayFinished() { }

  public exitGame(exitUrl = '') {
    if (exitUrl) {
      window.location.href = exitUrl;
    } else {
      window.history.back();
    }
  }

  public handleReload() {
    this.rootStore.gameStore.reloadGame();
  }

  public handleChangeGameInUrl() {
    console.warn('handleChangeGameInUrl method not implemented');
  }

  public changeGame(params: ChangeGamePayload): void {
    const { data: launchData } = this.rootStore.gameStore.launchData;
    if (!launchData) return;
    const { gameId, mode = launchData.mode, tournamentId, source } = params;
    const { partnerId, culture, gameId: fromGameId } = launchData;

    this.rootStore.lobbyStore.setIsLobbyOpen(false);
    this.rootStore.lobbyStore.setIsPreLobbyOpen(false);
    this.rootStore.tournamentStore.setTournamentFrameOpen(false);

    const isSameGame = +gameId === launchData.gameId;
    const tournamentModeSame = tournamentId ? +tournamentId === +(launchData.tournamentId || 0) : !launchData.tournamentId;

    if (isSameGame && isSameMode(launchData.mode, mode) && tournamentModeSame) return;

    this.rootStore.gameStore.changeGameIdInLaunchData(gameId);

    this.rootStore.gameStore.getGameLaunchInfo({
      mode,
      gameId,
      culture,
      partnerId,
      tournamentId,
    }, fromGameId, source);

    // reset necessary states
    this.rootStore.gameStore.setGameReady(false);
    this.rootStore.loaderStore.setGameLoaded(false);
    this.rootStore.loaderStore.setGameLoadedTrigger(false);
    this.rootStore.lobbyStore.resetPreLobbyGamesResponse();
  }
}

